import { ReportBundleFiles } from './types';
import { INPUT_SHEET_FILE, SCHEMA_REF_FILE, SchemaRef, schemaRefSource } from './inputSheet';
import { InputSheet } from '../inputs';
import { sampleRunReportContext } from './fixture';
import { generalLayout } from './generalLayout';

/**
 * What a new report bundle starts from:
 *
 * - `general`: the general procedure test report — a report for any run, with
 *   no code written for its procedure — as a composed report to rearrange and
 *   extend: its layout written out in layout.ts. Published as it is, it is
 *   the general report itself (the seed publishes it so); an author starts
 *   from it and changes what their procedure needs.
 * - `run`: a test report written from scratch, with a hand-written document.
 * - `inputs`: a report filled in from a schema, with no procedure run —
 *   multi-point input current over functions and supply voltages.
 * - `inputs-blank`: a report filled in from a schema that prints whatever the
 *   schema asks: over a new, empty schema, or one there is already.
 */
export const BUNDLE_STARTERS = ['general', 'run', 'inputs', 'inputs-blank'] as const;
export type BundleStarter = (typeof BUNDLE_STARTERS)[number];

/** The general procedure test report: the general starter, published as it is under this key (the seed does). */
export const GENERAL_REPORT = {
  key: 'procedure-test-report',
  title: 'General procedure test report',
  description: 'Works for any procedure: the run laid out as a test report — DUT information, facility, test information, setup, parameters, test data per step, conclusion with the run’s validations, and an appendix of data files — with the verdict taken from the run.',
} as const;

/**
 * A starter's files. The inputs starters name a new schema by `schemaKey` (the
 * bundle's key unless given) and carry its starting sheet as that schema's
 * draft, `inputs.json`; the blank one given `schema` reads that schema instead,
 * and carries no sheet of its own.
 */
export function starterBundleFiles(key: string, title: string, starter: BundleStarter = 'run', schemaKey: string = key, schema?: SchemaRef): ReportBundleFiles {
  if (starter === 'general') return generalStarterFiles(key, title);
  if (starter === 'inputs') return inputsStarterFiles(key, title, schemaKey);
  if (starter === 'inputs-blank') return blankInputsStarterFiles(key, title, schemaKey, schema);
  return runStarterFiles(key, title);
}

/**
 * The run starter: a test report, small enough to read in one sitting, that
 * reads its run rather than asking for data, written the way the SDK means
 * reports to be written. Values and settings are schemas; the settings are
 * the author's, set in code. What it reads from the run is declared
 * (`requires`), so the report template's page checks it against the
 * procedure. `judge` returns only the verdict, with finding kinds; the
 * document derives the rest. Sections number themselves, figures and tables
 * are numbered as they print, and the sign-off lines take typed names. A
 * fixture holds it to a verdict on every compile. Authors point it at their
 * procedure; it also serves as the compiler's own test subject.
 */
function runStarterFiles(key: string, title: string): ReportBundleFiles {
  return {
    'values.ts': RUN_VALUES,
    'evaluate.ts': RUN_EVALUATE,
    'definition.ts': runDefinition(key, title),
    'document.tsx': RUN_DOCUMENT,
    'fixtures/passing-run.json': RUN_FIXTURE,
  };
}

const RUN_VALUES = `import { ValuesOf, bindingsField, defineValues, v } from '@veriform/report-sdk';

/** What the report's owner fills in on each run; everything else comes from the run. */
export const values = defineValues({
  conclusion: v.text({ label: 'Conclusion', multiline: true }),
  signoffs: v.map(v.text(), { hidden: true }),
});

/** What every report this definition makes shares. Set here, in code: the limit, and any parameter a procedure names unusually. */
export const settings = defineValues({
  limit: v.number({ label: 'Upper limit', unit: 'A', default: 10, help: 'Each measurement is judged against it.' }),
  bindings: bindingsField(),
});

export type Values = ValuesOf<typeof values>;
export type Settings = ValuesOf<typeof settings>;
`;

const RUN_EVALUATE = `import { ResolvedBinding, allAttachments, need, RunSnapshot, toNumber, firstValue } from '@veriform/report-sdk';

/**
 * What the report reads from the run. The report template's page checks each
 * against the procedure: the parameter \`settings.bindings\` names for it, else
 * one of the identifiers.
 */
export const requires = {
  current: need.number('Measured current', { identifiers: ['measured_current'], many: true }),
};

export interface Measurement {
  step: string;
  value: number | null;
  pass: boolean | null;
}

/** Every measurement the run recorded, judged against the limit. Pure: the verdict and the document both read it. */
export function measurements(binding: ResolvedBinding | undefined, limit: number | null): Measurement[] {
  return (binding?.matches ?? []).map((m) => {
    const value = toNumber(firstValue(m.reading));
    return { step: m.step.title, value, pass: value === null || limit === null ? null : value <= limit };
  });
}

export interface Photo {
  fileId: string;
  fileName: string;
  caption: string;
}

export function photos(run: RunSnapshot): Photo[] {
  return allAttachments(run)
    .filter((a) => a.file.kind === 'image')
    .map((a) => ({ fileId: a.file.fileId, fileName: a.file.fileName, caption: a.reading.title || a.step.title }));
}
`;

function runDefinition(key: string, title: string): string {
  return `import { defineRunReport, finding, verdictFrom } from '@veriform/report-sdk';
import { measurements, requires } from './evaluate';
import { settings, values } from './values';

/**
 * A test report on one procedure run. A report template on a procedure
 * template makes it for that procedure's runs. To chart a waveform, add
 * \`traces(run)\` naming the points file to read — see "Results against
 * limits" in docs/REPORTS.md.
 */
export const definition = defineRunReport({
  key: ${JSON.stringify(key)},
  title: ${JSON.stringify(title)},
  description: 'One measurement from the run against a limit, the run\\u2019s photos, and a sign-off.',
  version: '0.1.0',
  values,
  settings,
  requires,
  signoffs: [
    { id: 'engineer', label: 'Test engineer' },
    { id: 'reviewer', label: 'Reviewer', optional: true },
  ],
  judge: ({ run, settings, bindings }) => {
    const measured = measurements(bindings.current, settings.limit);
    return verdictFrom([
      finding.check('run', run.status === 'PASSED', \`The run was recorded \${run.status}\`, \`The run was recorded \${run.status || 'without a result'}\`),
      measured.length ? finding.pass('measured', \`\${measured.length} measurement(s) recorded\`) : finding.gap('measured', 'The run records no measured current'),
      ...measured.filter((m) => m.pass === false).map((m, i) => finding.defect(\`over-\${i}\`, \`\${m.step}: \${m.value} A exceeds the limit of \${settings.limit} A\`)),
    ]);
  },
});
`;
}

const RUN_DOCUMENT = `import {
  DataColumn,
  DataTable,
  FigureIndex,
  ReportField,
  ReportGallery,
  ReportHeader,
  ReportSheet,
  ReportSummary,
  ReportViewProps,
  RunReportContext,
  ReportEvaluation,
  Section,
  SignOffs,
  StatusPill,
  SummaryGrid,
} from '@veriform/report-sdk';
import { definition } from './definition';
import { Measurement, photos, measurements } from './evaluate';
import { Values } from './values';

const measurementColumns: DataColumn<Measurement>[] = [
  { key: 'step', title: 'Step', render: (r) => r.step },
  { key: 'value', title: 'Measured (A)', render: (r) => (r.value === null ? '—' : String(r.value)), align: 'right' },
  { key: 'pass', title: 'Result', render: (r) => <StatusPill state={r.pass === null ? 'none' : r.pass ? 'pass' : 'fail'}>{r.pass === null ? 'NO DATA' : r.pass ? 'PASS' : 'FAIL'}</StatusPill> },
];

type Props = ReportViewProps<Values, ReportEvaluation, RunReportContext>;

/** The printed page. Sections number themselves, figures and tables are numbered as they print, and the result comes first. */
export function Document({ values, model, context, meta, onChange }: Props) {
  // the same inputs the verdict was judged from
  const { run, settings, bindings } = definition.read(values, context);
  const measured = measurements(bindings.current, settings.limit);
  const template = context.reportTemplate;

  return (
    <ReportSheet footer={<span>Report v{meta.reportVersion}{meta.versionNumber ? \` · published version \${meta.versionNumber}\` : ''}</span>}>
      <ReportHeader
        kind="Test report"
        verdict={model.verdict}
        verdictLabel={model.verdictLabel}
        title={template?.title || run.title}
        titleSub={run.sourceTitle ?? undefined}
        subtitle={[template?.requirement, template?.testMethod].filter(Boolean).join(' · ') || undefined}
        meta={[
          { label: 'Run', value: run.title },
          { label: 'Run status', value: run.status },
          { label: 'Limit', value: settings.limit === null ? 'not set' : \`\${settings.limit} A\` },
        ]}
      />
      <ReportSummary evaluation={model} tiles={[{ label: 'Measurements', value: String(measured.length) }]} />
      <Section id="results" title="Results">
        <DataTable title="Measured current against the limit" numbered columns={measurementColumns} rows={measured} rowKey={(r, i) => \`\${r.step}-\${i}\`} emptyText="Not recorded" />
      </Section>
      <Section id="dut" title="Device under test">
        <SummaryGrid items={[{ label: 'DUT', value: run.dut?.title ?? 'not recorded' }, { label: 'Project', value: context.project?.title ?? '—' }]} />
        <ReportGallery figures={photos(run)} />
      </Section>
      <Section id="conclusion" title="Conclusion">
        <ReportField id="conclusion" label="Conclusion" value={values.conclusion} onChange={onChange ? (v) => onChange({ ...values, conclusion: v }) : undefined} multiline />
        <SignOffs specs={definition.signoffs} values={values.signoffs} onChange={onChange ? (signoffs) => onChange({ ...values, signoffs }) : undefined} />
      </Section>
      <Section id="figures" title="Appendix — figure index" numbered={false}>
        <FigureIndex />
      </Section>
    </ReportSheet>
  );
}
`;

/** A run the report is held to on every compile: one passing measurement, well under the default limit. */
const RUN_FIXTURE = `${JSON.stringify({
  expect: { verdict: 'passed', verdictLabel: 'PASS' },
  values: { conclusion: 'Within the limit.' },
  context: {
    run: {
      id: '00000000-0000-0000-0000-00000000f1f1', title: 'Fixture run', status: 'PASSED', state: 'CREATED', completedAt: '2026-01-14T16:30:00.000Z',
      signedBy: 'tech@example.com', sourceTitle: 'Fixture procedure', projectId: null, dut: null,
      steps: [{ id: 's1', title: 'Measure the current', path: ['Stimulus and Data Acquisition'], status: 'PASSED', iteration: null, iterationOf: null,
        readings: [{ parameterId: 'p1', identifier: 'measured_current', title: 'Measured current', kind: 'output', values: [4.2], records: [], files: [] }] }],
    },
  },
}, null, 2)}
`;

// ── the inputs starter ──────────────────────────────────────────────────────

/**
 * The inputs starter: a report filled in by hand from its input sheet, with no
 * procedure run — the input current of a lamp, one reading per function at
 * each supply voltage, the report runner setting both. The sheet is a
 * schema, named in `schema.json`; `inputs.json` is its draft (the studio's
 * Inputs tab), saved to the schema rather than the bundle, and
 * `inputs.generated.ts` is written from it on save and types `inputs`. The
 * report's own values are what is asked on the report itself: remarks and the
 * sign-offs.
 */
function inputsStarterFiles(key: string, title: string, schemaKey: string): ReportBundleFiles {
  return {
    [SCHEMA_REF_FILE]: schemaRefSource({ schema: schemaKey }),
    [INPUT_SHEET_FILE]: `${JSON.stringify(INPUTS_SHEET, null, 2)}\n`,
    'values.ts': INPUTS_VALUES,
    'definition.ts': inputsDefinition(key, title),
    'document.tsx': INPUTS_DOCUMENT,
    'fixtures/within-limit.json': INPUTS_FIXTURE,
  };
}

export const INPUTS_SHEET: InputSheet = {
  sections: [
    {
      id: 'run_setup',
      title: 'Run setup',
      help: 'This bundle leaves the functions and supply voltages to the operator. Enter what you are testing today \u2014 the Measurements table builds its rows and columns from these.',
      inputs: [
        { id: 'functions', kind: 'names', label: 'Functions tested', defaults: ['Low beam', 'High beam', 'DRL'], required: true, carryOver: true },
        { id: 'supply_voltages', kind: 'numbers', label: 'Supply voltages', unit: 'V', defaults: [9, 13.5, 16], required: true, carryOver: true },
      ],
    },
    {
      id: 'unit_under_test',
      title: 'Unit under test',
      inputs: [
        { id: 'application', kind: 'text', label: 'Application', required: true, carryOver: true },
        { id: 'part_number', kind: 'text', label: 'Part number', required: true, carryOver: true },
        { id: 'serial_number', kind: 'text', label: 'Serial number', required: true },
        { id: 'sample_phase', kind: 'choice', label: 'Sample phase', choices: ['A-sample', 'B-sample', 'C-sample', 'D-sample'], carryOver: true },
        { id: 'dut_photos', kind: 'image', label: 'DUT photos', multiple: true },
      ],
    },
    {
      id: 'test_conditions',
      title: 'Test conditions',
      inputs: [
        { id: 'ambient_temperature', kind: 'number', label: 'Ambient temperature', unit: '\u00b0C', required: true, carryOver: true },
        { id: 'test_date', kind: 'date', label: 'Test date', required: true },
        { id: 'chamber_settled', kind: 'yesno', label: 'Chamber settled before test?' },
        {
          id: 'equipment',
          kind: 'table',
          label: 'Equipment used',
          help: 'A row per instrument',
          carryOver: true,
          columns: [
            { id: 'instrument', title: 'Instrument', type: 'string' },
            { id: 'asset_id', title: 'Asset ID', type: 'string' },
            { id: 'calibration_due', title: 'Calibration due', type: 'string' },
          ],
          rows: [
            { instrument: 'DC power supply', asset_id: null, calibration_due: null },
            { instrument: 'Current probe', asset_id: null, calibration_due: null },
            { instrument: 'Oscilloscope', asset_id: null, calibration_due: null },
          ],
        },
      ],
    },
    {
      id: 'measurements',
      title: 'Measurements',
      inputs: [
        {
          id: 'input_current',
          kind: 'sweep',
          label: 'Input current',
          help: 'One reading per function \u00d7 supply voltage from Run setup, in amps.',
          required: true,
          conditions: [
            { id: 'function', label: 'Function', input: 'functions' },
            { id: 'supply_voltage', label: 'Supply voltage', input: 'supply_voltages' },
          ],
          record: [{ id: 'current', kind: 'number', label: 'Input current', unit: 'A' }],
          layout: { tabs: [], rows: ['function'], columns: ['supply_voltage'] },
        },
      ],
    },
    {
      id: 'data_files',
      title: 'Data files',
      inputs: [
        { id: 'current_capture', kind: 'waveform', label: 'Current capture', help: 'CSV or scope export, time vs. current', xLabel: 'Time (s)', yLabel: 'Current (A)' },
        { id: 'setup_photo', kind: 'image', label: 'Setup photo', required: true },
      ],
    },
  ],
};

const INPUTS_VALUES = `import { ValuesOf, defineValues, v } from '@veriform/report-sdk';

/** What is asked on the report itself, not the sheet: remarks, and the sign-offs by role. */
export const values = defineValues({
  remarks: v.text({ label: 'Remarks', multiline: true, optional: true }),
  signoffs: v.map(v.text(), { hidden: true }),
});

/** What every report this definition makes shares, set here in code. */
export const settings = defineValues({
  limit: v.number({ label: 'Maximum input current', unit: 'A', default: 1, help: 'Each reading is judged against it.' }),
});

export type Values = ValuesOf<typeof values>;
export type Settings = ValuesOf<typeof settings>;
`;

function inputsDefinition(key: string, title: string): string {
  return `import { defineInputsReport, finding, sweepGrid, verdictFrom, describeCell } from '@veriform/report-sdk';
import { sheet } from './inputs.generated';
import { settings, values } from './values';

/**
 * A report filled in by hand: no procedure run, only what the operator
 * entered on the input sheet (inputs.json). Each reading of the input-current
 * sweep is judged against the limit; a reading not yet entered leaves the
 * report incomplete.
 */
export const definition = defineInputsReport({
  key: ${JSON.stringify(key)},
  title: ${JSON.stringify(title)},
  description: 'Input current per function at each supply voltage, entered by hand, against a limit.',
  version: '0.1.0',
  sheet,
  values,
  settings,
  signoffs: [
    { id: 'engineer', label: 'Test engineer' },
    { id: 'reviewer', label: 'Quality reviewer', optional: true },
  ],
  judge: ({ inputs, settings }) => {
    const sweep = sheet.sections.flatMap((s) => s.inputs).find((i) => i.id === 'input_current');
    const grid = sweep?.kind === 'sweep' ? sweepGrid(sheet, sweep, inputs) : null;
    const cells = grid?.cells ?? [];
    const missing = cells.filter((c) => typeof c.values.current !== 'number');
    const over = cells.filter((c) => typeof c.values.current === 'number' && settings.limit !== null && c.values.current > settings.limit);
    return verdictFrom([
      cells.length ? finding.pass('readings', \`\${cells.length - missing.length} of \${cells.length} readings entered\`) : finding.gap('readings', 'No functions or supply voltages to measure'),
      ...(missing.length ? [finding.gap('missing', \`\${missing.length} reading(s) not entered\`)] : []),
      ...over.map((c, i) => finding.defect(\`over-\${i}\`, \`\${describeCell(grid?.conditions ?? [], c.at)}: \${c.values.current} A exceeds the limit of \${settings.limit} A\`)),
    ]);
  },
});
`;
}

const INPUTS_DOCUMENT = `import {
  DataColumn,
  DataTable,
  FigureIndex,
  InputsReportContext,
  LimitChart,
  ReportEvaluation,
  ReportField,
  ReportGallery,
  ReportHeader,
  ReportSheet,
  ReportSummary,
  ReportViewProps,
  Section,
  SignOffs,
  SummaryGrid,
  formatConditionValue,
  sweepGrid,
} from '@veriform/report-sdk';
import { definition } from './definition';
import { sheet } from './inputs.generated';
import { Values } from './values';

type Props = ReportViewProps<Values, ReportEvaluation, InputsReportContext>;

const or = (text: string | null | undefined, fallback = '\u2014') => (text && text.trim() ? text : fallback);

interface EquipmentRow { instrument: string | null; asset_id: string | null; calibration_due: string | null }
const equipmentColumns: DataColumn<EquipmentRow>[] = [
  { key: 'instrument', title: 'Instrument', render: (r) => or(r.instrument) },
  { key: 'asset_id', title: 'Asset ID', render: (r) => or(r.asset_id) },
  { key: 'calibration_due', title: 'Calibration due', render: (r) => or(r.calibration_due) },
];

/** The printed page: the unit, the conditions, the readings against the limit, the capture and the sign-off. */
export function Document({ values, model, context, meta, onChange }: Props) {
  const { inputs, settings } = definition.read(values, context);
  const sweep = sheet.sections.flatMap((s) => s.inputs).find((i) => i.id === 'input_current');
  const grid = sweep?.kind === 'sweep' ? sweepGrid(sheet, sweep, inputs) : null;
  const [functionCondition, voltageCondition] = grid?.conditions ?? [];
  const readingColumns: DataColumn<string>[] = [
    { key: 'function', title: 'Function', render: (name) => name },
    ...(voltageCondition?.values ?? []).map((voltage) => ({
      key: String(voltage),
      title: formatConditionValue(voltageCondition, voltage),
      align: 'right' as const,
      render: (name: string) => {
        const current = grid?.cell({ function: name, supply_voltage: voltage }).values.current;
        return typeof current === 'number' ? \`\${current} A\` : '\u2014';
      },
    })),
  ];
  const capture = context.traces.current_capture;
  const photos = inputs.dut_photos.map((f, i) => ({ fileId: f.fileId, fileName: f.fileName, caption: \`Unit under test (\${i + 1})\` }));

  return (
    <ReportSheet footer={<span>Report v{meta.reportVersion}{meta.versionNumber ? \` · published version \${meta.versionNumber}\` : ''}</span>}>
      <ReportHeader
        kind="Test report"
        verdict={model.verdict}
        verdictLabel={model.verdictLabel}
        title={context.report?.title ?? definition.title}
        subtitle={[inputs.application, inputs.part_number].filter(Boolean).join(' \u00b7 ') || undefined}
        meta={[
          { label: 'Serial', value: or(inputs.serial_number) },
          { label: 'Sample phase', value: or(inputs.sample_phase) },
          { label: 'Tested', value: or(inputs.test_date) },
          { label: 'Limit', value: settings.limit === null ? 'not set' : \`\${settings.limit} A\` },
        ]}
      />
      <ReportSummary evaluation={model} tiles={[{ label: 'Readings', value: String(grid?.cells.filter((c) => typeof c.values.current === 'number').length ?? 0) }]} />
      <Section id="results" title="Input current">
        <DataTable title="Input current by function and supply voltage" numbered columns={readingColumns} rows={functionCondition?.values.map(String) ?? []} rowKey={(name) => name} emptyText="No functions entered" />
      </Section>
      <Section id="unit" title="Unit under test">
        <SummaryGrid items={[
          { label: 'Application', value: or(inputs.application) },
          { label: 'Part number', value: or(inputs.part_number) },
          { label: 'Serial number', value: or(inputs.serial_number) },
          { label: 'Sample phase', value: or(inputs.sample_phase) },
        ]} />
        <ReportGallery figures={photos} />
      </Section>
      <Section id="conditions" title="Test conditions">
        <SummaryGrid items={[
          { label: 'Ambient temperature', value: inputs.ambient_temperature === null ? '\u2014' : \`\${inputs.ambient_temperature} \u00b0C\` },
          { label: 'Test date', value: or(inputs.test_date) },
          { label: 'Chamber settled', value: inputs.chamber_settled === null ? '\u2014' : inputs.chamber_settled ? 'Yes' : 'No' },
        ]} />
        <DataTable title="Equipment used" numbered columns={equipmentColumns} rows={inputs.equipment} rowKey={(r, i) => \`\${r.instrument}-\${i}\`} emptyText="No equipment listed" />
      </Section>
      <Section id="captures" title="Data files">
        {capture
          ? <LimitChart points={capture.points} xLabel={capture.xLabel ?? 'Time (s)'} yLabel={capture.yLabel ?? 'Current (A)'} ariaLabel="Current capture" caption={capture.fileName} />
          : <p>No capture attached.</p>}
        <ReportGallery figures={inputs.setup_photo.map((f) => ({ fileId: f.fileId, fileName: f.fileName, caption: 'Test setup' }))} />
      </Section>
      <Section id="conclusion" title="Conclusion & sign-off">
        <ReportField id="remarks" label="Remarks" value={values.remarks} onChange={onChange ? (v) => onChange({ ...values, remarks: v }) : undefined} multiline optional />
        <SignOffs specs={definition.signoffs} values={values.signoffs} onChange={onChange ? (signoffs) => onChange({ ...values, signoffs }) : undefined} />
      </Section>
      <Section id="figures" title="Appendix \u2014 figure index" numbered={false}>
        <FigureIndex />
      </Section>
    </ReportSheet>
  );
}
`;

/** Every reading entered and under the default limit of 1 A. */
const INPUTS_FIXTURE = `${JSON.stringify({
  expect: { verdict: 'passed', verdictLabel: 'PASS' },
  values: { remarks: 'All functions within the limit.' },
  inputs: {
    functions: ['Low beam', 'High beam', 'DRL'],
    supply_voltages: [9, 13.5, 16],
    application: 'Example headlamp',
    part_number: 'PN-0000',
    serial_number: 'SN-0001',
    ambient_temperature: 23,
    test_date: '2026-01-14',
    input_current: {
      cells: [
        ['Low beam', 9, 0.612], ['Low beam', 13.5, 0.418], ['Low beam', 16, 0.357],
        ['High beam', 9, 0.884], ['High beam', 13.5, 0.596], ['High beam', 16, 0.509],
        ['DRL', 9, 0.231], ['DRL', 13.5, 0.16], ['DRL', 16, 0.14],
      ].map(([fn, voltage, current]) => ({ at: { function: fn, supply_voltage: voltage }, values: { current } })),
    },
  },
}, null, 2)}
`;

// ── the blank inputs starter ────────────────────────────────────────────────

/** The sheet a new, empty schema starts as: one section to add inputs to. */
export const EMPTY_SHEET: InputSheet = { sections: [{ id: 'general', title: 'General', inputs: [] }] };

/**
 * The blank inputs starter: a report filled in by hand that prints whatever
 * its schema asks, section by section, and comes to INCOMPLETE until every
 * required input is entered. It reads no input by id, so it compiles over
 * any schema: a new, empty one (`inputs.json` the empty sheet), or one there
 * is already (`ref`: only `schema.json`, naming it), to start a report from.
 */
function blankInputsStarterFiles(key: string, title: string, schemaKey: string, ref?: SchemaRef): ReportBundleFiles {
  return {
    [SCHEMA_REF_FILE]: schemaRefSource(ref ?? { schema: schemaKey }),
    ...(ref ? {} : { [INPUT_SHEET_FILE]: `${JSON.stringify(EMPTY_SHEET, null, 2)}\n` }),
    'values.ts': BLANK_INPUTS_VALUES,
    'definition.ts': blankInputsDefinition(key, title),
    'document.tsx': BLANK_INPUTS_DOCUMENT,
    'fixtures/nothing-entered.json': `${JSON.stringify({ inputs: {}, values: {} }, null, 2)}\n`,
  };
}

const BLANK_INPUTS_VALUES = `import { ValuesOf, defineValues, v } from '@veriform/report-sdk';

/** What is asked on the report itself, not the schema: remarks, and the sign-offs by role. */
export const values = defineValues({
  remarks: v.text({ label: 'Remarks', multiline: true, optional: true }),
  signoffs: v.map(v.text(), { hidden: true }),
});

export type Values = ValuesOf<typeof values>;
`;

function blankInputsDefinition(key: string, title: string): string {
  return `import { defineInputsReport, finding, sheetProgress, verdictFrom } from '@veriform/report-sdk';
import { sheet } from './inputs.generated';
import { values } from './values';

/**
 * A report filled in by hand: no procedure run, only what was entered on its
 * schema. It is complete once every required input is entered; judge readings
 * against limits here as the report grows.
 */
export const definition = defineInputsReport({
  key: ${JSON.stringify(key)},
  title: ${JSON.stringify(title)},
  description: '',
  version: '0.1.0',
  sheet,
  values,
  signoffs: [{ id: 'engineer', label: 'Test engineer' }],
  judge: ({ inputs }) => {
    const missing = sheetProgress(sheet, inputs).missing.length;
    return verdictFrom([missing ? finding.gap('missing', missing + ' required input(s) not entered') : finding.pass('entered', 'Every required input entered')]);
  },
});
`;
}

const BLANK_INPUTS_DOCUMENT = `import {
  DataColumn,
  DataTable,
  InputsReportContext,
  ReportEvaluation,
  ReportField,
  ReportHeader,
  ReportSheet,
  ReportSummary,
  ReportViewProps,
  Section,
  SheetInput,
  SignOffs,
  SummaryGrid,
  SweepInput,
  TableInput,
  describeCell,
  sweepGrid,
} from '@veriform/report-sdk';
import { definition } from './definition';
import { sheet } from './inputs.generated';
import { Values } from './values';

type Props = ReportViewProps<Values, ReportEvaluation, InputsReportContext>;
type Entered = Record<string, unknown>;

/** A value as it prints; a dash for one not entered. */
function show(value: unknown, unit?: string): string {
  if (value === null || value === undefined || value === '') return '\\u2014';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map((v) => show(v, unit)).join(', ') : '\\u2014';
  if (typeof value === 'object') return 'fileName' in value ? String(value.fileName) : JSON.stringify(value);
  return unit ? String(value) + ' ' + unit : String(value);
}

const unitOf = (input: object) => ('unit' in input && typeof input.unit === 'string' ? input.unit : undefined);

function InputTable({ input, rows }: { input: TableInput; rows: Entered[] }) {
  const columns: DataColumn<Entered>[] = input.columns.map((c) => ({ key: c.id, title: c.title, render: (row) => show(row[c.id]) }));
  return <DataTable title={input.label} numbered columns={columns} rows={rows} rowKey={(_row, i) => String(i)} emptyText="Nothing entered" />;
}

function SweepTable({ input, entered }: { input: SweepInput; entered: Entered }) {
  const grid = sweepGrid(sheet, input, entered);
  type Cell = (typeof grid.cells)[number];
  const columns: DataColumn<Cell>[] = [
    { key: 'at', title: 'At', render: (cell) => describeCell(grid.conditions, cell.at) },
    ...input.record.map((field) => ({ key: field.id, title: field.label, render: (cell: Cell) => show(cell.values[field.id], unitOf(field)) })),
  ];
  return <DataTable title={input.label} numbered columns={columns} rows={grid.cells} rowKey={(cell) => describeCell(grid.conditions, cell.at)} emptyText="Nothing to record" />;
}

/** One section's inputs: values in a grid, tables and sweeps each a table of their own. */
function SectionInputs({ inputs, entered }: { inputs: SheetInput[]; entered: Entered }) {
  const values = inputs.filter((i) => i.kind !== 'table' && i.kind !== 'sweep');
  return (
    <>
      {values.length > 0 && <SummaryGrid items={values.map((i) => ({ label: i.label, value: show(entered[i.id], unitOf(i)) }))} />}
      {inputs.map((i) => {
        if (i.kind === 'table') return <InputTable key={i.id} input={i} rows={Array.isArray(entered[i.id]) ? (entered[i.id] as Entered[]) : []} />;
        if (i.kind === 'sweep') return <SweepTable key={i.id} input={i} entered={entered} />;
        return null;
      })}
    </>
  );
}

/** The printed page: every section of the schema as it was filled in, then the remarks and sign-off. */
export function Document({ values, model, context, meta, onChange }: Props) {
  const { inputs } = definition.read(values, context);
  const entered = inputs as unknown as Entered;
  return (
    <ReportSheet footer={<span>Report v{meta.reportVersion}</span>}>
      <ReportHeader kind="Test report" verdict={model.verdict} verdictLabel={model.verdictLabel} title={context.report?.title ?? definition.title} />
      <ReportSummary evaluation={model} />
      {sheet.sections.map((section) => (
        <Section key={section.id} id={section.id} title={section.title}>
          <SectionInputs inputs={section.inputs} entered={entered} />
        </Section>
      ))}
      <Section id="sign-off" title="Remarks & sign-off">
        <ReportField id="remarks" label="Remarks" value={values.remarks} onChange={onChange ? (v) => onChange({ ...values, remarks: v }) : undefined} multiline optional />
        <SignOffs specs={definition.signoffs} values={values.signoffs} onChange={onChange ? (signoffs) => onChange({ ...values, signoffs }) : undefined} />
      </Section>
    </ReportSheet>
  );
}
`;

// ── the general starter ─────────────────────────────────────────────────────

/**
 * The general starter: the general procedure test report's layout
 * (generalLayout.ts, written out as source), definition and document. Tests
 * compile it and hold it to what the general report prints
 * (reportBundleCompiler.test.ts, generalReport.evaluate.test.ts).
 */
function generalStarterFiles(key: string, title: string): ReportBundleFiles {
  return {
    'layout.ts': generalLayoutSource(),
    'definition.ts': generalDefinition(key, title),
    'document.tsx': GENERAL_DOCUMENT,
    'fixtures/sample-run.json': `${JSON.stringify({ expect: { verdict: 'passed', verdictLabel: 'PASS' }, context: sampleRunReportContext() }, null, 2)}\n`,
  };
}

const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
const LINE = 140;
const quote = (text: string) => `'${text.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n')}'`;
const keyOf = (key: string) => (IDENTIFIER.test(key) ? key : quote(key));
const entriesOf = (value: object) => Object.entries(value).filter(([, x]) => x !== undefined);

function inline(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return quote(value);
  if (typeof value !== 'object') return String(value);
  if (Array.isArray(value)) return `[${value.map(inline).join(', ')}]`;
  const entries = entriesOf(value);
  return entries.length ? `{ ${entries.map(([k, x]) => `${keyOf(k)}: ${inline(x)}`).join(', ')} }` : '{}';
}

/** A value as TypeScript source, the way this codebase writes it: on one line where it fits, else one entry a line. */
function toSource(value: unknown, indent = ''): string {
  const one = inline(value);
  if (value === null || typeof value !== 'object' || indent.length + one.length <= LINE) return one;
  const next = `${indent}  `;
  if (Array.isArray(value)) return `[\n${value.map((x) => `${next}${toSource(x, next)},`).join('\n')}\n${indent}]`;
  const entries = entriesOf(value);
  // the plain values an object opens with share a line: `id: 'setup', title: 'Test setup', numbered: true,`
  const lead = entries.findIndex(([, x]) => x !== null && typeof x === 'object');
  const head = entries.slice(0, lead === -1 ? entries.length : lead);
  const shared = head.length > 1 ? [`${next}${head.map(([k, x]) => `${keyOf(k)}: ${inline(x)}`).join(', ')},`] : [];
  const rest = (shared.length ? entries.slice(head.length) : entries).map(([k, x]) => `${next}${keyOf(k)}: ${toSource(x, next)},`);
  return `{\n${[...shared, ...rest].join('\n')}\n${indent}}`;
}

function generalLayoutSource(): string {
  return `import { Dimension, ReportLayout } from '@veriform/report-sdk';

/**
 * The report's sections, in print order, and the blocks in each: the general
 * procedure test report's layout, as every run of every procedure gets it.
 * Rearrange it, rename sections, drop the ones this procedure never fills,
 * and add blocks — the SDK's (results against limits, the limits themselves,
 * a capture against limits, a results matrix, coverage …) or the report's
 * own. A block set to \`whenEmpty: 'leave-out'\` prints nothing when the run
 * gives it nothing; a section left empty goes with it.
 */
export function defaultLayout(): ReportLayout {
  return ${toSource(generalLayout(), '  ')};
}

/**
 * What the procedure repeats its steps over, for the blocks that lay results
 * out by condition (\`groupBy\`, \`rows\`, \`cols\`, \`split\`). A dimension
 * takes its values from one of the procedure's own lists — the entries the
 * run selected — or names them, with the words a step title uses for each:
 *
 *   { id: 'temperature', label: 'Temperature', parameter: 'temperatures', values: [] },
 *   { id: 'supply', label: 'Supply', values: [{ label: 'Minimum', match: ['9 V'] }, { label: 'Nominal', match: ['13.5 V'] }] },
 */
export const dimensions: Dimension[] = [];
`;
}

function generalDefinition(key: string, title: string): string {
  return `import { dataFileTraces, defineComposedReport, need, runFindings, runLabels } from '@veriform/report-sdk';
import { defaultLayout, dimensions } from './layout';

/**
 * A test report on one procedure run, started from the general procedure test
 * report: it prints what that report prints until its layout (layout.ts)
 * says otherwise. Its verdict is the run's own result (\`runFindings\`) and
 * every check a block in the layout makes; add checks of its own in
 * \`findings\`, and block types of its own in \`blocks\`, with their views
 * in document.tsx.
 */
export const definition = defineComposedReport({
  key: ${JSON.stringify(key)},
  title: ${JSON.stringify(title)},
  description: 'A test report on a run, started from the general procedure test report.',
  version: '0.1.0',
  defaultLayout,
  dimensions,
  // the DUT's parameters bind by the identifiers every template uses
  requires: {
    application: need.role('dut.application', { optional: true }),
    assembly: need.role('dut.assembly', { optional: true }),
    partNumber: need.role('dut.partNumber', { optional: true }),
    serial: need.role('dut.serial', { optional: true }),
    samplePhase: need.role('dut.samplePhase', { optional: true }),
    location: need.role('dut.location', { optional: true }),
    hardwareVersion: need.role('dut.hardwareVersion', { optional: true }),
    softwareVersion: need.role('dut.softwareVersion', { optional: true }),
    image: need.role('dut.image', { optional: true, many: true }),
  },
  signoffs: [
    { id: 'testEngineer', label: 'Test engineer' },
    { id: 'reviewer', label: 'Reviewer', optional: true },
  ],
  // the data files of steps with no picture of their own, drawn as charts where they were recorded
  traces: (run) => dataFileTraces(run),
  findings: (c) => runFindings(c.run),
  labels: (c) => runLabels(c.run),
});
`;
}

const GENERAL_DOCUMENT = `import { ComposedDocument, ReportEvaluation, ReportValues, ReportViewProps, RunReportContext, reportTitle, requirementLine, runCounts, runDocControl } from '@veriform/report-sdk';
import { definition } from './definition';

/**
 * The report as it prints: the header, the result first with the tiles that
 * frame it, document control, then the layout's sections. Block types of the
 * report's own draw through \`views\` (by type). A tile can read a block's
 * model: \`limitsOf(definition.compose(values, context), 'results')?.worst\`.
 */
export function Document(props: ReportViewProps<ReportValues, ReportEvaluation, RunReportContext>) {
  const { context, meta } = props;
  const run = context.run;
  const counts = runCounts(run);
  return (
    <ComposedDocument
      {...props}
      definition={definition}
      header={{
        kind: 'Test report',
        title: reportTitle(context),
        titleSub: run.sourceTitle ?? undefined,
        subtitle: requirementLine(context) || undefined,
        meta: [
          { label: 'Run', value: run.title },
          ...(context.project ? [{ label: 'Project', value: context.project.title }] : []),
          { label: 'Run status', value: run.status },
          { label: 'Signed by', value: run.signedBy ?? 'not recorded' },
        ],
        docControl: runDocControl(context),
      }}
      summaryTiles={[
        { label: 'Steps', value: String(counts.steps), sub: counts.failedSteps ? \`\${counts.failedSteps} failed\` : 'none failed', state: counts.failedSteps ? 'fail' : undefined },
        {
          label: 'Checks',
          value: String(counts.checks),
          sub: counts.failedValidations ? \`\${counts.failedValidations} failed\` : counts.noes ? \`\${counts.noes} recorded No\` : counts.checks ? 'all passed' : 'none recorded',
          state: counts.failedValidations ? 'fail' : undefined,
        },
      ]}
      views={{}}
      footer={<>Generated by Veriform · {definition.title} v{meta.reportVersion}</>}
    />
  );
}
`;
